import { Directive, HostListener, inject, Input } from '@angular/core';
import { FormData } from '@lx/core/forms/form-data.model';
import { NotificationService } from '@lx/shared/logger';
import { CustomHostListeners } from '@lx/shared/utils';

/**
 * Manages the validity of a dropdown-backed FormData.
 *
 * The field is validated only when the dropdown is closed:
 * - If a value is selected → the field is valid
 * - If no value is selected → the field is invalid
 *
 * This avoids relying on focus/blur events, which are unreliable for dropdowns.
 */
@Directive({
  standalone: true,
  selector: '[lxFormDataDropdownValidity]',
})
export class FormDataDropdownValidityDirective<T> {
  @Input() formData: FormData<T> | undefined;

  private readonly notificationService = inject(NotificationService);

  /**
   * Trigger validation when the dropdown closes.
   */
  @HostListener(`${CustomHostListeners.dropdownClosed}`)
  onDropdownClosed(): void {
    this.updateValidity();
  }

  /**
   * Updates the FormData validity based on the presence of a value.
   * If the FormData is missing, a warning is logged.
   */
  private updateValidity(): void {
    if (!this.formData) {
      this.notificationService.warn('FormDataDropdownValidityDirective: formData input is not provided');
      return;
    }

    this.formData.isValid = this.hasValue(this.formData.value);
  }

  /**
   * Determines whether the provided value should be considered valid.
   */
  private hasValue(value: T | null | undefined): boolean {
    return value !== null && value !== undefined && value !== '';
  }
}
